import React, { useCallback, useEffect, useState } from 'react';
import {
  Panel, DataTable, Tag, Button, IconButton, Dialog, Field, TextInput, TextArea, Switch, Segmented, FileDrop, EmptyState,
  ckConfirm, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { apiError } from '../../next/ui';

/**
 * Exam print assets in the new design — design elements (images: top / bottom / frame) and cover / end pages (PDF).
 * One panel, two configs; same endpoints and actions as ExamBlanks / ExamExtraPages:
 * upload, edit (with optional file replace), position, default (star), active toggle, download and delete.
 */
const CONFIGS = {
  blanks: {
    endpoint: '/exam-blanks',
    title: 'אלמנטים עיצוביים',
    sub: 'תמונות שמשמשות כאלמנט עיצובי בהדפסת בחינות',
    icon: 'image',
    noun: 'אלמנט',
    addLabel: 'העלאת תבנית',
    firstLabel: 'העלאת אלמנט ראשון',
    accept: '.png,.jpg,.jpeg,.webp',
    fileHint: 'png, jpg, webp',
    namePlaceholder: 'לדוג׳: בלאנק ניסן תשפ״ו',
    defaultLabel: 'הגדר כתבנית ברירת מחדל',
    positions: [{ value: 'top', label: 'למעלה', tone: 'success' }, { value: 'bottom', label: 'למטה', tone: 'primary' }, { value: 'frame', label: 'כמסגרת', tone: 'warning' }],
    loadError: 'שגיאה בטעינת אלמנטים',
    emptyTitle: 'אין אלמנטים עדיין',
    emptyText: 'העלה תמונה שתשמש כאלמנט עיצובי בהדפסת בחינות',
    toggled: (on) => (on ? 'תבנית הופעלה' : 'תבנית הושבתה'),
    fallbackName: (t) => `blank-${t.name}.pdf`,
  },
  extraPages: {
    endpoint: '/exam-extra-pages',
    title: 'דפי שער וסיום (PDF)',
    sub: 'קבצי PDF שיתווספו לפני או אחרי הבחינה בהנפקה',
    icon: 'file',
    noun: 'דף',
    addLabel: 'העלאת דף PDF',
    firstLabel: 'העלאת דף ראשון',
    accept: '.pdf,application/pdf',
    fileHint: 'PDF',
    namePlaceholder: 'לדוג׳: שער מוסד / דף הוראות',
    defaultLabel: 'הגדר כברירת מחדל למיקום זה',
    positions: [{ value: 'cover', label: 'דף שער (לפני)', short: 'דף שער', tone: 'primary' }, { value: 'end', label: 'דף סיום (אחרי)', short: 'דף סיום', tone: 'danger' }],
    loadError: 'שגיאה בטעינת דפי שער/סיום',
    emptyTitle: 'אין דפי שער/סיום עדיין',
    emptyText: 'העלה קובץ PDF לשימוש חוזר כדף שער או דף סיום',
    toggled: (on) => (on ? 'דף הופעל' : 'דף הושבת'),
    fallbackName: (t) => `${t.name}.pdf`,
  },
};

export function ExamAssetsPanel({ kind }) {
  const cfg = CONFIGS[kind];
  const toast = useCkToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // { editing, name, desc, file, isDefault, position }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(cfg.endpoint);
      setRows(data.data || []);
    } catch {
      setError(cfg.loadError);
    } finally {
      setLoading(false);
    }
  }, [cfg]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setDialog({ editing: null, name: '', desc: '', file: null, isDefault: false, position: cfg.positions[0].value });
  const openEdit = (t) => setDialog({ editing: t, name: t.name, desc: t.description || '', file: null, isDefault: !!t.isDefault, position: t.position || cfg.positions[0].value });

  const save = async () => {
    if (!dialog.name.trim()) { toast.error('שם חובה'); return; }
    if (!dialog.editing && !dialog.file) { toast.error(kind === 'extraPages' ? 'יש להעלות קובץ PDF' : 'יש להעלות קובץ'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', dialog.name.trim());
      fd.append('description', dialog.desc.trim());
      fd.append('isDefault', String(dialog.isDefault));
      fd.append('position', dialog.position);
      if (dialog.file) fd.append('file', dialog.file);
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (dialog.editing) await api.put(`${cfg.endpoint}/${dialog.editing._id}`, fd, { headers });
      else await api.post(cfg.endpoint, fd, { headers });
      toast.success(`${cfg.noun} ${dialog.editing ? 'עודכן' : 'נוסף'} בהצלחה`);
      setDialog(null);
      load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירה'));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (t) => {
    if (!(await ckConfirm({ title: `מחיקת ${cfg.noun}`, message: `למחוק את ה${cfg.noun} "${t.name}"?`, confirmLabel: 'מחיקה', danger: true }))) return;
    try {
      await api.delete(`${cfg.endpoint}/${t._id}`);
      toast.success(`${cfg.noun} נמחק`);
      load();
    } catch {
      toast.error('שגיאה במחיקה');
    }
  };
  const downloadFile = async (t) => {
    try {
      const response = await api.get(`${cfg.endpoint}/${t._id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = t.fileOriginalName || cfg.fallbackName(t);
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('שגיאה בהורדה');
    }
  };
  const patch = async (t, body, message) => {
    try {
      await api.put(`${cfg.endpoint}/${t._id}`, body);
      toast.success(message);
      load();
    } catch {
      toast.error('שגיאה בעדכון');
    }
  };

  const pos = (v) => cfg.positions.find((p) => p.value === v) || cfg.positions[0];
  const columns = [
    { key: 'name', label: 'שם', render: (t) => <b>{t.name}</b> },
    { key: 'description', label: 'תיאור', getValue: (t) => t.description || '' },
    { key: 'position', label: 'מיקום', type: 'enum', getValue: (t) => pos(t.position).short || pos(t.position).label, render: (t) => <Tag tone={pos(t.position).tone}>{pos(t.position).short || pos(t.position).label}</Tag> },
    {
      key: 'status',
      label: 'סטטוס',
      type: 'enum',
      getValue: (t) => (t.status === 'active' ? 'פעיל' : 'מושבת'),
      render: (t) => <Switch checked={t.status === 'active'} onChange={() => patch(t, { status: t.status === 'active' ? 'inactive' : 'active' }, cfg.toggled(t.status !== 'active'))} label={t.status === 'active' ? 'פעיל' : 'מושבת'} />,
    },
    {
      key: 'isDefault',
      label: 'ברירת מחדל',
      type: 'bool',
      render: (t) => (
        <IconButton
          icon="flag"
          label={t.isDefault ? 'הסר ברירת מחדל' : 'הגדר כברירת מחדל'}
          active={!!t.isDefault}
          onClick={(e) => { e.stopPropagation(); patch(t, { isDefault: !t.isDefault }, t.isDefault ? 'הוסר כברירת מחדל' : 'הוגדר כברירת מחדל'); }}
        />
      ),
    },
  ];

  return (
    <Panel
      title={cfg.title}
      sub={cfg.sub}
      icon={cfg.icon}
      flush
      actions={<Button size="sm" variant="primary" icon="plus" onClick={openCreate}>{cfg.addLabel}</Button>}
    >
      {!loading && !error && rows.length === 0 ? (
        <EmptyState icon={cfg.icon} title={cfg.emptyTitle} action={<Button icon="plus" onClick={openCreate}>{cfg.firstLabel}</Button>}>{cfg.emptyText}</EmptyState>
      ) : (
        <DataTable
          mode="client"
          rows={rows}
          rowKey="_id"
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          onRefresh={load}
          storageKey={`bchinot-exam-${kind}`}
          selectable={false}
          searchable={rows.length > 8}
          exportFilename={cfg.title}
          onRowClick={openEdit}
          rowActions={(t) => [
            { key: 'dl', icon: 'download', label: 'הורד', onClick: () => downloadFile(t) },
            { key: 'edit', icon: 'edit', label: 'ערוך', onClick: () => openEdit(t) },
            { key: 'del', icon: 'trash', label: 'מחק', onClick: () => remove(t) },
          ]}
        />
      )}

      <Dialog
        open={!!dialog}
        onClose={() => !saving && setDialog(null)}
        title={dialog?.editing ? `עריכת ${kind === 'extraPages' ? 'דף PDF' : 'אלמנט'}` : `העלאת ${kind === 'extraPages' ? 'דף PDF' : 'אלמנט'} חדש`}
        icon="upload"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={saving} onClick={() => setDialog(null)}>ביטול</Button>
            <Button variant="primary" icon="upload" disabled={saving} onClick={save}>{saving ? 'שומר…' : (dialog?.editing ? 'עדכן' : 'העלה')}</Button>
          </>
        )}
      >
        {dialog ? (
          <div className="nx-stack">
            <Field label={kind === 'extraPages' ? 'שם הדף' : 'שם האלמנט'}><TextInput value={dialog.name} placeholder={cfg.namePlaceholder} data-autofocus onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))} /></Field>
            <Field label="תיאור (אופציונלי)"><TextArea rows={2} value={dialog.desc} onChange={(e) => setDialog((d) => ({ ...d, desc: e.target.value }))} /></Field>
            <FileDrop
              accept={cfg.accept}
              icon={cfg.icon}
              title={dialog.file ? dialog.file.name : (dialog.editing ? `החלף קובץ (אופציונלי)` : `בחר קובץ (${cfg.fileHint})…`)}
              hint={dialog.file ? 'לחצו להחלפה' : `גררו לכאן או לחצו לבחירה · ${cfg.fileHint}`}
              onFile={(f) => setDialog((d) => ({ ...d, file: f || null }))}
            />
            <Field label={kind === 'extraPages' ? 'מיקום הדף' : 'מיקום האלמנט'}>
              <Segmented value={dialog.position} onChange={(v) => setDialog((d) => ({ ...d, position: v }))} items={cfg.positions.map((p) => ({ value: p.value, label: p.label }))} />
            </Field>
            <Switch checked={dialog.isDefault} onChange={(v) => setDialog((d) => ({ ...d, isDefault: v }))} label={cfg.defaultLabel} />
          </div>
        ) : null}
      </Dialog>
    </Panel>
  );
}

/** ExamBlanks in the new design: design elements + cover / end pages (as the legacy screen stacks them). */
export function ExamBlanksNext() {
  return (
    <div className="nx-stack">
      <ExamAssetsPanel kind="blanks" />
      <ExamAssetsPanel kind="extraPages" />
    </div>
  );
}

export function ExamExtraPagesNext() {
  return <ExamAssetsPanel kind="extraPages" />;
}
